import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import flowDocumentService from '../../../../../services/flowDocumentService';
import { FlowDocument, FlowDocumentFilter } from '../../../../../services/types/Flow';
import { Button, CheckBox, LinkButton, Modal, SpinnerArea } from '../../../../../shared/components';
import ConfirmModal from '../../../../../shared/components/ConfirmModal';
import FileUploadButton from '../../../../../shared/components/FileUploadButton';
import Table, { Cell } from '../../../../../shared/components/Table';
import useServiceReducer from '../../../../../shared/hooks/useServiceReducer';

import { dateStr, genericSort, isDraft } from '../../../../../shared/misc/utils';

import Context from '../../../../App/MainProvider/context';
import OperationContext from '../../context';
import reducer from './reducer';

interface Props {
  flowId: number;
  flowLap: number;
  flowVersion: number;
  editable: boolean;
  isFlowApproved: boolean;
}

type PropsType = Props;

const FlowDocuments: React.FC<PropsType> = (props) => {
  const { flowId, flowLap, flowVersion, editable, isFlowApproved } = props;

  const [docSelected, setDocSelected] = useState<FlowDocument | null>(null);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { t } = useTranslation();
  const { service, inSuperUserMode, notify } = useContext(Context);
  const { operation } = useContext(OperationContext);

  const [formData, setFormData] = useState<FormData | null>(null);
  const [errorUpload, setErrorUpload] = useState<string | null>(null);
  const [formDataIsAideMemoire, setFormDataIsAideMemoire] = useState(false);

  const [data, loading, , dispatch, , reload] = useServiceReducer(
    () => flowDocumentService.getDocuments(flowId, flowLap),
    reducer,
    undefined
  );

  const canEdit = (operation.isMember || inSuperUserMode()) && editable && !isDraft(operation);

  const initFilter: FlowDocumentFilter = {
    sortField: 'documentName',
    sortDescending: false,
  };

  const [filter, setFilter] = useState(initFilter);

  const changeSort = (column: string, descending: boolean) => {
    if (data) {
      const aideMemoireItems = [...data.filter((x) => x.aideMemoire)];

      const sortedList = aideMemoireItems.concat(
        genericSort(
          [...data.filter((x) => !x.aideMemoire)],
          column as keyof FlowDocument,
          descending
        )
      );

      dispatch({ type: 'ORDER', data: sortedList });

      setFilter((prev) => ({ ...filter, sortField: column, sortDescending: !prev.sortDescending }));
    }
  };

  const clearFormUpload = () => {
    setFormData(null);
    setErrorUpload(null);
    setFormDataIsAideMemoire(false);
  };

  const preUpload = (fData: FormData) => {
    setErrorUpload(null);
    setFormData(fData);
  };

  const uploadFileError = (textError: string) => {
    const aideMemo = formDataIsAideMemoire;
    clearFormUpload();

    setFormDataIsAideMemoire(aideMemo);
    let error = textError;
    if (error.toLocaleLowerCase() === 'oversize') {
      error = t('uploadMaxSizeError30');
    }
    setErrorUpload(error);
  };

  const upload = () => {
    if (formData) {
      if (formDataIsAideMemoire) {
        formData.append('aideMemorie', 'true');
      }
      setIsSubmitting(true);

      service(() => flowDocumentService.uploadDocument(flowId, flowLap, flowVersion, formData), {
        callback: (result: boolean) => {
          clearFormUpload();
          if (result) {
            reload();
          }
        },
        loader: setIsSubmitting,
      });
    }
  };

  const download = (docId: number) => {
    service(() => flowDocumentService.downloadDocument(flowId, flowLap, docId), {
      loader: setIsSubmitting,
    });
  };

  const showConfirmDelete = (obj: FlowDocument) => {
    setConfirmDeleteVisible(true);
    setDocSelected({ ...obj });
  };
  const closeConfirmDelete = () => {
    setConfirmDeleteVisible(false);
    setDocSelected(null);
  };

  const deleteDoc = () => {
    if (!docSelected) {
      return;
    }
    setIsSubmitting(true);

    service(() => flowDocumentService.deleteDocument(flowId, flowLap, docSelected.id), {
      callback: () => {
        setDocSelected(null);
        setConfirmDeleteVisible(false);
        reload();
      },
      loader: setIsSubmitting,
    });
  };

  const copyLinkToClipboard = (link: string) => {
    navigator.clipboard.writeText(link);
    notify(t('linkCopied'), '', 'info');
  };

  const cells: Cell<FlowDocument>[] = [
    {
      name: 'documentName',
      column: 'document',
      value: (x) => x.documentName,
    },
    {
      name: 'aideMemoire',
      column: 'aideMemoire',
      sortDisabled: true,
      render: (_, obj) =>
        obj.aideMemoire ? (
          <div className="badge-base flowstatus aide-memoire">
            <div className="badge-text">
              <span>{t('aideMemoire')}</span>
            </div>
          </div>
        ) : (
          <></>
        ),
    },
    { name: 'createdBy', column: 'modifiedBy', value: (x) => x.createdBy },
    { name: 'createdOn', column: 'modifiedDate', value: (x) => dateStr(x.createdOn) },
    {
      column: 'actions',
      css: 'upload-flow-documents-table__actions',
      render: (x, obj) => (
        <>
          {obj.documentLink ? (
            <LinkButton
              key={`copy_link_${obj.id}`}
              onClick={() => copyLinkToClipboard(obj.documentLink)}
              icon="link"
              title={t('copyLink')}
            />
          ) : null}
          <LinkButton
            key={`download_document_${obj.id}`}
            onClick={() => download(obj.id)}
            icon="file-download"
          />
          {(!isFlowApproved && canEdit) || (isFlowApproved && canEdit && obj.aideMemoire) ? (
            <LinkButton
              key={`delete_document_${obj.id}`}
              onClick={() => showConfirmDelete(obj)}
              icon="trash-alt"
            />
          ) : null}
        </>
      ),
    },
  ];

  return (
    <SpinnerArea loading={loading || isSubmitting}>
      <div className="details-bar my-0 flow-actions">
        <div className="mr-auto" />
        {canEdit && (
          <Button
            className="btn-primary"
            text={t('uploadNew')}
            onClick={() => setUploadModalVisible(true)}
          />
        )}
      </div>
      {data && (
        <Table
          data={data}
          cells={cells}
          keyFunc={(x) => x.id}
          sortData={filter}
          changeSort={changeSort}
          discapitalize
          emptyNoDesc
        />
      )}
      <ConfirmModal
        title={t('deleteDocument')}
        buttonText={t('delete')}
        visible={confirmDeleteVisible}
        setVisible={closeConfirmDelete}
        action={() => deleteDoc()}
      >
        {t('deleteDocumentMsg', { file: docSelected && docSelected.documentName })}
      </ConfirmModal>
      <Modal
        title={t('uploadNewDocument')}
        titleUnderline
        actions={[
          {
            text: t('cancel'),
            action: clearFormUpload,
          },
          {
            text: t('uploadDocument'),
            action: upload,
            disabled: formData === null,
          },
        ]}
        visible={uploadModalVisible}
        setVisible={setUploadModalVisible}
        onClosed={() => setErrorUpload(null)}
      >
        <div className="upload-flow-documents-form">
          <div className="form">
            <div className="form__form-group mb-0">
              <div className="form__form-group-field">
                <div className="form__form-group-input-wrap">
                  <input
                    placeholder={formData ? (formData.get('file') as File).name : t('addDocument')}
                    type="text"
                    readOnly
                  />
                </div>
                <FileUploadButton
                  htmlId="btnUpload"
                  text={t('selectFile')}
                  onClick={preUpload}
                  className="upload-flow-documents float-right"
                  onError={uploadFileError}
                />
              </div>
            </div>
            {errorUpload && <span className="form__form-group-error">{errorUpload}</span>}
          </div>
        </div>
        <div className="d-block clearfix">
          <CheckBox
            element={{
              id: 'isAideMemoire',
              text: t('isAideMemoire'),
              checked: formDataIsAideMemoire,
            }}
            onChange={(id: string, value: boolean) => setFormDataIsAideMemoire(value)}
            disabled={data ? data.some((x) => x.aideMemoire) : false}
          />
        </div>
      </Modal>
    </SpinnerArea>
  );
};

export default FlowDocuments;
